import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { DisplayNameEditor } from "@/components/DisplayNameEditor";
import { BragCard } from "@/components/BragCard";
import { InviteButton } from "@/components/InviteButton";
import { Trophy, Crown, ChevronLeft, Target, Crosshair, ArrowUp, ArrowDown, UserPlus } from "lucide-react";

export default function GlobalLeaderboard() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [notStarted, setNotStarted] = useState(false);
  const [startMd, setStartMd] = useState(5);
  const [inviters, setInviters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/leaderboard/global")
      .then(({ data }) => {
        setRows(data.leaderboard); setTotal(data.total_players);
        setNotStarted(!!data.not_started);
        if (data.scoring_start_matchday) setStartMd(data.scoring_start_matchday);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get("/leaderboard/inviters").then(({ data }) => setInviters(data.leaderboard || [])).catch(() => {});
  }, []);

  const medal = (rank) => (rank === 1 ? "text-[hsl(var(--stadium-yellow))]" : rank === 2 ? "text-zinc-300" : rank === 3 ? "text-amber-600" : "text-muted-foreground");
  const myRank = rows.find((r) => r.is_me)?.rank;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b-2 border-border px-4 sm:px-8 py-4 flex items-center justify-between">
        <Link to="/app" data-testid="leaderboard-back" className="inline-flex items-center gap-1 text-sm uppercase tracking-wider text-muted-foreground hover:text-foreground">
          <ChevronLeft size={16} /> Back
        </Link>
        <div className="flex items-center gap-2">
          <Trophy className="text-primary" size={20} />
          <h1 className="font-display text-2xl uppercase tracking-wide">Global Leaderboard</h1>
        </div>
        <span className="text-xs font-data text-muted-foreground hidden sm:block">{total} playing</span>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-8">
        <p className="overline text-primary mb-1">Top 10 gaffers worldwide</p>
        <p className="text-xs text-muted-foreground font-data mb-4">Ranked by prediction accuracy, tie-broken by exact correct scores. Tied fans are joint.</p>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <DisplayNameEditor />
          <div className="flex items-center gap-2">
            <InviteButton />
            <BragCard rank={myRank} />
          </div>
        </div>

        <div className="mt-4 border-2 border-foreground" data-testid="global-leaderboard">
          <div className="grid grid-cols-[3rem_1fr_5rem_5rem] items-center px-4 py-2 border-b-2 border-foreground bg-card">
            <span className="overline text-muted-foreground">#</span>
            <span className="overline text-muted-foreground">Gaffer</span>
            <span className="overline text-muted-foreground text-end flex items-center justify-end gap-1"><Target size={11} /> Acc</span>
            <span className="overline text-muted-foreground text-end flex items-center justify-end gap-1"><Crosshair size={11} /> Exact</span>
          </div>
          {loading ? (
            <p className="px-4 py-10 text-center text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="leaderboard-empty">
              {notStarted
                ? `⚽ Pre-season — no games scored yet. The table unlocks when Matchday ${startMd} kicks off.`
                : "No results yet — the table fills up once matches are played."}
            </p>
          ) : (
            rows.map((r) => (
              <div key={r.user_id} data-testid={`gl-row-${r.rank}`}
                className={`grid grid-cols-[3rem_1fr_5rem_5rem] items-center px-4 py-3 border-b border-border ${r.is_me ? "bg-primary/10" : ""}`}>
                <span className={`font-display text-xl flex items-center gap-1 ${medal(r.rank)}`}>
                  {r.rank <= 3 && r.points > 0 && <Crown size={14} />}{r.joint ? "=" : ""}{r.rank}
                </span>
                <span className="truncate font-medium flex items-center gap-2">
                  {r.name}
                  {r.is_me && <span className="text-[9px] uppercase tracking-wider bg-primary text-primary-foreground px-1">You</span>}
                  {r.joint && <span className="text-[9px] uppercase tracking-wider text-[hsl(var(--stadium-yellow))] border border-[hsl(var(--stadium-yellow))]/50 px-1" data-testid={`gl-joint-${r.rank}`}>Joint</span>}
                </span>
                <span className="font-data font-bold text-lg text-end text-primary">{r.points}</span>
                <span className="font-data text-end text-muted-foreground flex items-center justify-end gap-1">
                  {r.movement != null && r.movement !== 0 && (
                    r.movement > 0
                      ? <span className="text-[hsl(var(--pitch-green))] text-[10px] flex items-center" data-testid={`gl-move-${r.rank}`}><ArrowUp size={11} />{r.movement}</span>
                      : <span className="text-destructive text-[10px] flex items-center" data-testid={`gl-move-${r.rank}`}><ArrowDown size={11} />{Math.abs(r.movement)}</span>
                  )}
                  {r.exact}
                </span>
              </div>
            ))
          )}
        </div>

        {/* Top inviters */}
        <div className="mt-10">
          <p className="overline text-[hsl(var(--pitch-green))] mb-1 flex items-center gap-1"><UserPlus size={12} /> Top inviters</p>
          <p className="text-xs text-muted-foreground font-data mb-4">Fans bringing the most mates into GAFFER. Every invite builds the banter.</p>
          <div className="border-2 border-foreground" data-testid="inviter-leaderboard">
            {inviters.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="inviters-empty">
                No invites landed yet — be the first to bring your mates in.
              </p>
            ) : (
              inviters.map((r) => (
                <div key={r.user_id} data-testid={`inv-row-${r.rank}`}
                  className={`grid grid-cols-[3rem_1fr_5rem] items-center px-4 py-3 border-b border-border last:border-0 ${r.is_me ? "bg-[hsl(var(--pitch-green))]/10" : ""}`}>
                  <span className={`font-display text-xl ${medal(r.rank)}`}>{r.rank}</span>
                  <span className="truncate font-medium flex items-center gap-2">
                    {r.name}
                    {r.is_me && <span className="text-[9px] uppercase tracking-wider bg-[hsl(var(--pitch-green))] text-black px-1">You</span>}
                  </span>
                  <span className="font-data font-bold text-lg text-end text-[hsl(var(--pitch-green))]">{r.count}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
